/**
 * Generates a prescription scan name based on prescription details and testWorkQueueId.
 * @param {Object} prescription - Prescription object containing customer details, number_text, and id.
 * @param {string} testWorkQueueId - The test work queue ID to include in the scan name.
 * @returns {string} Formatted prescription scan name or empty string on error.
 */
export function getPrescriptionScanName(prescription, testWorkQueueId) {
    try {
      const {
        customer: { first_name: firstName = '' } = {},
        customer: { last_name: lastName = '' } = {},
        number_text: numberText = '',
      } = prescription || {};

      const normalizedFirstName = prescription.customer_name || firstName;
      const normalizedLastName = prescription.customer_last_name || lastName;

      const now = new Date();
      const year = String(now.getFullYear());
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const dateTime = `${year}${month}${day}_${hours}${minutes}`;

      const formattedFirstName = normalizedFirstName.trim().replace(/\s+/g, '_');
      const formattedLastName = normalizedLastName.trim().replace(/\s+/g, '_');

      return `${dateTime}_${formattedFirstName}_${formattedLastName}_${numberText}_${testWorkQueueId}`;
    } catch (err) {
      console.error('Error in getPrescriptionScanName:', err);
      return '';
    }
  }

  /**
   * Copies the prescription scan name to the clipboard.
   * @param {string} fileName - The prescription scan name to copy.
   * @param {Object} options - Options including i18n, success, and error callbacks.
   * @param {Function} options.i18n - Translation function (e.g., vue-i18n's t).
   * @param {Function} options.success - Success toast notification function.
   * @param {Function} options.error - Error toast notification function.
   * @returns {Promise<void>}
   */
  export async function copyPrescriptionScanName(fileName, { i18n, success, error }) {
    try {
      await navigator.clipboard.writeText(fileName);
      success(i18n('File name copied successfully!'));
    } catch (err) {
      error(i18n(err.message) || err.message);
    }
  }
